import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Observable, of } from 'rxjs'
import { AppState } from 'src/app/core/core.state'
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms'
import { Store } from '@ngrx/store'
import { configurationOptionSelectors } from '../../configuration-option.selectors'
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  take,
  timeout
} from 'rxjs/operators'
import { environment } from 'src/environments/environment'
import { Actions, ofType } from '@ngrx/effects'
import { NotificationService } from 'src/app/core/notification.service'
import { ConfigurationOptionService } from '../../configuration-option.service'
import { UserAccessRightService } from 'src/app/shared/services/user-access-rights.service'
import { AccessRights } from 'src/app/features/role/user-role/user-role'
import { configurationOptionActions } from '../../configuration-option.actions'
import { DEFAULT_STYLE_SETTINGS } from 'src/app/shared/constants'
import { TranslateService } from '@ngx-translate/core'
@UntilDestroy()
@Component({
  selector: 'configuration-option-branding-settings',
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss']
})
export class ConfigurationOptionBrandingButtonsCardComponent implements OnInit {
  @Output() onSubmit = new EventEmitter<any>()
  @Input() formValues: any
  @Input() isCollapsed: any = false
  @Input() isloadingFromParentPage: any = false

  public formValues$: Observable<any>
  public isLoading$: Observable<boolean>
  public form: UntypedFormGroup
  public accessRights = AccessRights
  public availableLanguages = environment.frontendLanguages.reduce(
    (acc, curr) => {
      if (curr != 'xx') return ((acc[curr] = true), acc)
      else return acc
    },
    {}
  )
  public formFieldsEditeds = {}
  public boldWeightOptions = [
    { name: 'Thin (200)', value: 200 },
    { name: 'Regular (400)', value: 400 },
    { name: 'Bold (600)', value: 600 },
    { name: 'Extra bold (800)', value: 800 }
  ]
  public objectKeys = Object.keys
  public parsedFormValues: any = null
  private originalFormValues

  constructor(
    private store: Store<AppState>,
    private fb: UntypedFormBuilder,
    private actions$: Actions,
    private notificationService: NotificationService,
    public accessRightService: UserAccessRightService,
    private configOptionService: ConfigurationOptionService,
    private translate: TranslateService
  ) {}
  config: any = {
    placeholder: '',
    tabsize: 2,
    dialogsInBody: true,
    height: '200px',
    toolbar: [
      ['misc', ['codeview', 'undo', 'redo']],
      [
        'font',
        [
          'bold',
          'italic',
          'underline',
          'strikethrough',
          'superscript',
          'subscript',
          'clear'
        ]
      ],
      ['fontsize', ['fontname', 'fontsize', 'color']],
      ['para', ['style', 'ul', 'ol', 'paragraph', 'height']],
      ['insert', ['table', 'picture', 'link', 'video', 'hr']]
    ],
    fontNames: [
      'Helvetica',
      'Arial',
      'Arial Black',
      'Comic Sans MS',
      'Courier New',
      'Roboto',
      'Times',
      'opensans',
      'sansserif',
      'monospace',
      'serif'
    ]
  }

  ngOnInit() {
    this.formValues$ = this.store
      .select(configurationOptionSelectors.selectAll)
      .pipe(
        distinctUntilChanged((a, b) => JSON.stringify(a) == JSON.stringify(b)),
        map((configOptions) =>
          this.configOptionService.mapEntitiesToObject(configOptions)
        )
      )

    this.form = this.fb.group({})

    this.isLoading$ = this.store.select(
      configurationOptionSelectors.selectIsLoading
    )

    this.formValues$
      .pipe(
        filter((formValues) => formValues?.custom_style_settings),
        untilDestroyed(this)
      )
      .subscribe((formValues) => {
        this.parsedFormValues = JSON.parse(formValues.custom_style_settings)

        this.patchFormValues()
      })

    this.form.valueChanges.pipe(untilDestroyed(this)).subscribe((data) => {
      this.formValues$.pipe(take(1)).subscribe((formValues) => {
        const keysChanged = this.compareObjects(data, formValues)
        keysChanged.forEach((keys) => {
          this.formFieldsEditeds[keys] = this.form.controls[keys].value
        })
      })
    })
  }

  private patchFormValues() {
    const modifiedKeysObject = {}
    for (const key in this.parsedFormValues) {
      if (Object.hasOwnProperty.call(this.parsedFormValues, key)) {
        const newKey = key.replace('.', '')
        modifiedKeysObject[newKey] = this.parsedFormValues[key]
      }
    }

    this.parsedFormValues = modifiedKeysObject

    Object.keys(this.parsedFormValues).forEach((key) => {
      const formGroup = this.fb.group({})

      Object.keys(this.parsedFormValues[key]).forEach((controlName) => {
        let controlValue = this.parsedFormValues[key][controlName]

        // convert incoming rgba values to hex (color input works only with hex values)
        if (controlValue.toString().includes(',')) {
          const isRgb = controlValue.match(/^\d{1,3},\d{1,3},\d{1,3}$/)

          if (isRgb) {
            let [r, g, b] = controlValue.split(',').map(Number)

            const toHex = (n) => n.toString(16).padStart(2, '0')

            const hex = `#${toHex(r)}${toHex(g)}${toHex(b)}`

            this.parsedFormValues[key][controlName] = hex
          }
        }

        if (
          controlName == '--bs-btn-font-weight' ||
          controlName == '--bs-nav-link-font-weight'
        ) {
          const matchedOption = this.boldWeightOptions.find((option) => {
            return option.value == controlValue
          })

          if (matchedOption) {
            controlValue = {
              name: matchedOption.name,
              value: matchedOption.value
            }
          }
        }

        // set validators
        let validators = []

        switch (controlName) {
          case '--bs-btn-border-width':
            validators = [Validators.min(0), Validators.max(10)]
            break
          case '--bs-btn-border-radius':
            validators = [Validators.min(0), Validators.max(100)]
            break
          default:
            validators = []
        }

        formGroup.addControl(
          controlName,
          this.fb.control(controlValue, validators)
        )
      })

      const keyWithoutDot = key.replace('.', '')
      this.form.addControl(keyWithoutDot, formGroup)
    })

    const valuesToPatch = { ...this.parsedFormValues }

    if (valuesToPatch.btn) {
      if (valuesToPatch.btn['--bs-btn-font-weight']) {
        const fontWeightValue = valuesToPatch.btn['--bs-btn-font-weight']
        const matchedOption = this.boldWeightOptions.find(
          (option) => option.value == fontWeightValue
        )
        valuesToPatch.btn['--bs-btn-font-weight'] = matchedOption || {
          name: `${fontWeightValue}`,
          value: fontWeightValue
        }
      }

      if (valuesToPatch.btn['--bs-btn-border-width']?.length) {
        // convert rem value to px
        if (valuesToPatch.btn['--bs-btn-border-width'].includes('rem')) {
          valuesToPatch.btn['--bs-btn-border-width'] =
            parseFloat(valuesToPatch.btn['--bs-btn-border-width']) * 16
        }
        // remove 'px' from value
        else {
          valuesToPatch.btn['--bs-btn-border-width'] = valuesToPatch.btn[
            '--bs-btn-border-width'
          ].replace('px', '')
        }
      }

      if (valuesToPatch.btn['--bs-btn-border-radius']?.length) {
        // convert rem value to px
        if (valuesToPatch.btn['--bs-btn-border-radius'].includes('rem')) {
          valuesToPatch.btn['--bs-btn-border-radius'] =
            parseFloat(valuesToPatch.btn['--bs-btn-border-radius']) * 16
        }
        // remove 'px' from value
        else {
          valuesToPatch.btn['--bs-btn-border-radius'] = valuesToPatch.btn[
            '--bs-btn-border-radius'
          ].replace('px', '')
        }
      }
    }

    if (
      valuesToPatch.navbar &&
      valuesToPatch.navbar['--bs-nav-link-font-weight']
    ) {
      const fontWeightValue = valuesToPatch.navbar['--bs-nav-link-font-weight']
      const matchedOption = this.boldWeightOptions.find(
        (option) => option.value == fontWeightValue
      )
      valuesToPatch.navbar['--bs-nav-link-font-weight'] = matchedOption || {
        name: `${fontWeightValue}`,
        value: fontWeightValue
      }
    }

    this.form.setValue(valuesToPatch)

    this.originalFormValues = valuesToPatch
  }

  public toggleColorInput(
    controlName: string,
    formGroupName: string,
    event: Event
  ) {
    const target = event.target as HTMLInputElement
    const formGroup = this.form.controls[formGroupName] as UntypedFormGroup

    const control = formGroup.get(controlName)

    if (target.checked) {
      control.setValue('transparent')
    } else {
      const originalValue =
        this.originalFormValues?.[formGroupName]?.[controlName]

      control.setValue(
        originalValue == 'transparent'
          ? this.getDefaultValue(formGroupName, controlName)
          : originalValue
      )
    }
  }

  private getDefaultValue(formGroupName, controlName) {
    let groupName = formGroupName

    if (groupName != 'root') {
      groupName = '.' + groupName
    }

    const defaultValue = DEFAULT_STYLE_SETTINGS[groupName]?.[controlName]

    return defaultValue == 'transparent' ? '#000000' : defaultValue
  }

  public onFormSubmit(): void {
    if (!this.form.valid) {
      return
    }

    const objectToSave = {}

    for (const key in this.form.value) {
      const newKey = key == 'root' ? key : '.' + key
      objectToSave[newKey] = { ...this.form.value[key] }

      if (objectToSave[newKey]['--bs-btn-border-color'] !== undefined) {
        const checkbox = document.getElementById(
          `checkbox-${key}`
        ) as HTMLInputElement

        if (checkbox.checked) {
          objectToSave[newKey]['--bs-btn-border-color'] = 'transparent'
        }
      }

      const propertiesWeNeedAlsoInRgb = [
        '--bs-primary-color',
        '--bs-secondary-color',
        '--bs-danger-color',
        '--bs-success-color',
        '--bs-warning-color',
        '--bs-info-color'
      ]

      propertiesWeNeedAlsoInRgb.forEach((property) => {
        if (key == 'root' && objectToSave[newKey][property] !== undefined) {
          const rgbProperty = property.replace('-color', '-rgb')

          objectToSave[newKey][rgbProperty] = this.convertToRgb(
            objectToSave[newKey],
            property
          )
        }
      })

      if (objectToSave[newKey]['--bs-btn-hover-border-color'] !== undefined) {
        const checkbox = document.getElementById(
          `checkbox-${key}-hover`
        ) as HTMLInputElement

        if (checkbox.checked) {
          objectToSave[newKey]['--bs-btn-hover-border-color'] = 'transparent'
        }
      }

      this.addUnitToValue(objectToSave[newKey], '--bs-btn-border-width', 'px')
      this.addUnitToValue(objectToSave[newKey], '--bs-btn-border-radius', 'px')

      this.formatFontWeight(objectToSave[newKey], '--bs-btn-font-weight')
      this.formatFontWeight(objectToSave[newKey], '--bs-nav-link-font-weight')
    }

    this.configOptionService.updateOrCreateMany({
      custom_style_settings: JSON.stringify(objectToSave)
    })

    this.actions$
      .pipe(
        ofType(configurationOptionActions.upsertMany),
        take(1),
        timeout(4000),
        catchError((error) => of(null))
      )
      .subscribe((action) =>
        action
          ? this.notificationService.emit('alerts.success', 'success')
          : null
      )
  }

  public resetToDefault() {
    const message = this.translate.instant(
      'configurationOptionModule.resetConfirmation'
    )
    if (!window.confirm(message)) {
      return
    }

    this.parsedFormValues = DEFAULT_STYLE_SETTINGS

    this.patchFormValues()
  }

  private compareObjects(
    obj1: Record<string, any>,
    obj2: Record<string, any>
  ): string[] {
    const changedKeys: string[] = []

    // Check each key in obj1 and compare its value to obj2
    for (const key in obj1) {
      if (obj1.hasOwnProperty(key)) {
        if (
          obj1[key] !== obj2[key] &&
          (obj1[key] || typeof obj1[key] === 'undefined') &&
          (obj2[key] || typeof obj2[key] === 'undefined')
        ) {
          changedKeys.push(key)
        }
      }
    }

    return changedKeys
  }

  private convertToRgb(object: any, property: string): string {
    if (object[property] !== undefined) {
      return this.hexToRgb(object[property])
    }
  }

  private addUnitToValue(object: any, property: string, unit: string) {
    if (object[property] !== undefined) {
      object[property] += unit
    }
  }

  private formatFontWeight(object: any, property: string) {
    if (object[property] !== undefined && object[property]?.value) {
      object[property] = object[property]?.value
    }
  }

  private hexToRgb(hex: string): string {
    const bigint = parseInt(hex.slice(1), 16)
    const r = (bigint >> 16) & 255
    const g = (bigint >> 8) & 255
    const b = bigint & 255

    return `${r},${g},${b}`
  }
}
